import { createSignal, type JSX } from 'solid-js';
import { InfoBox } from './InfoBox';
import { SettingsCog } from './SettingsCog';
import { srcLink } from '../app-constants';
import './SongBar.css';

const SEARCH_INFO = `
<h2>Song Search</h2>
<p>Search for MIDI files across online libraries. Results stream in as you type.</p>
<h3>Sources</h3>
<table>
<tr><td><strong>GitHub</strong></td><td>Open-source MIDI collections</td></tr>
<tr><td><strong>Midishare</strong></td><td>Community-contributed MIDI arrangements</td></tr>
</table>
${srcLink('midi-search.lit.md', 'Source: midi-search.lit.md')}`;

const QUANT_INFO = `
<h2>Quantization</h2>
<p>Snaps note timings to a beat grid for Piano Tiles-style gameplay.</p>
<table>
<tr><td><strong>None</strong></td><td>Raw MIDI timing</td></tr>
<tr><td><strong>1/4</strong></td><td>Quarter note grid — beginner</td></tr>
<tr><td><strong>1/8</strong></td><td>Eighth note — intermediate</td></tr>
<tr><td><strong>1/16</strong></td><td>Sixteenth note — advanced</td></tr>
</table>
${srcLink('game-engine.lit.md', 'Source: game-engine.lit.md')}`;

const MAXKEYS_INFO = `
<h2>Max Simultaneous Keys</h2>
<p>Keyboard rollover limit. Most laptops: 3-6. Gaming keyboards: 10+. MIDI: no limit.</p>
<p>Game engine trims chords to this size. Default 8.</p>
${srcLink('game-engine.lit.md', 'Source: game-engine.lit.md')}`;

const CALIBRATE_INFO = `
<h2>Calibrate Playable Area</h2>
<p>Defines which notes your input device can reach. After calibration, unreachable cells grey out and the game auto-transposes.</p>
<ol>
<li>Click <strong>Calibrate</strong> — cells turn grey</li>
<li>Play every reachable note — cells light up</li>
<li><strong>Confirm</strong> to save, <strong>Cancel</strong> to discard</li>
</ol>
${srcLink('calibration.lit.md', 'Source: calibration.lit.md')}`;

export interface SongBarProps {
  onSearch: (query: string) => void;
  onSearchFocus: () => void;
  onSearchBlur: () => void;
  onQuantizationCycle: (value: string) => void;
  onGameReset: () => void;
  onCalibrateStart: () => void;
  onCalibrateConfirm: () => void;
  onCalibrateCancel: () => void;
  onMaxKeysChange: (n: number) => void;
  initialMaxKeys?: number;
}

export function SongBar(props: SongBarProps): JSX.Element {
  const [maxKeys, setMaxKeys] = createSignal(props.initialMaxKeys ?? 8);
  const [gameSettingsOpen, setGameSettingsOpen] = createSignal(false);
  const [calibrating, setCalibrating] = createSignal(false);

  const handleSearchInput = (e: Event): void => {
    props.onSearch((e.target as HTMLInputElement).value);
  };

  const handleMaxKeysChange = (e: Event): void => {
    const val = Math.max(1, parseInt((e.target as HTMLInputElement).value, 10) || 8);
    setMaxKeys(val);
    props.onMaxKeysChange(val);
  };

  const handleFileUpload = (e: Event): void => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file?.name.endsWith('.mid') || file?.name.endsWith('.midi')) {
      document.getElementById('song-bar')?.dispatchEvent(new CustomEvent('midi-file-upload', { detail: file, bubbles: true }));
    }
    input.value = '';
  };


  return (
    <div id="song-bar">

      <div id="song-bar-search">
        <div class="search-row">
        <InfoBox infoKey="search" infoContent={SEARCH_INFO}>
        <div class="search-input-wrap">
          <i
            data-lucide="search"
            class="search-icon"
          />
          <input
            id="midi-search-input"
            type="text"
            placeholder="Search or drop .mid file..."
            onInput={handleSearchInput}
            onFocus={() => { props.onSearchFocus(); }}
            onBlur={() => { props.onSearchBlur(); }}
          />
        </div>
        <input
          type="file"
          id="midi-file-input"
          accept=".mid,.midi"
          onChange={handleFileUpload}
        />
        <button
          class="icon-btn icon-md upload-btn"
          onClick={() => { document.getElementById('midi-file-input')?.click(); }}
          aria-label="Upload MIDI file"
        >
          <i data-lucide="upload" />
        </button>
        <div
          id="midi-search-results"
        />
        </InfoBox>
        </div>
      </div>

      <div id="song-bar-status">
        <SettingsCog
          id="game-settings-btn"
          active={gameSettingsOpen()}
          onClick={() => { if (!calibrating()) setGameSettingsOpen(v => !v); }}
          position="relative"
        />
        <div class="game-settings-popup" classList={{ hidden: !gameSettingsOpen() && !calibrating() }}>
          <InfoBox infoKey="quantization" infoContent={QUANT_INFO}>
            <span class="text-dim-sm">Quant</span>
            <span id="quantization-select-slot" />
          </InfoBox>
          <InfoBox infoKey="maxkeys" infoContent={MAXKEYS_INFO}>
            <label class="maxkeys-label">
              Max Keys
              <input
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                id="max-keys-input"
                value={maxKeys()}
                onChange={handleMaxKeysChange}
              />
            </label>
          </InfoBox>
          <InfoBox infoKey="calibrate" infoContent={CALIBRATE_INFO}>
            <div class="calibrate-wrap">
              <button
                id="calibrate-btn"
                onClick={() => { setCalibrating(true); setGameSettingsOpen(true); props.onCalibrateStart(); }}
              >
                Calibrate Playable Area
              </button>
              <span id="calibration-banner">
                <span id="calibration-warning" />
                <span id="calibration-msg">Play all notes</span>
                <button
                  id="calibrate-confirm"
                  onClick={() => { setCalibrating(false); props.onCalibrateConfirm(); }}
                >
                  &#x2713;
                </button>
                <button
                  id="calibrate-cancel"
                  onClick={() => { setCalibrating(false); props.onCalibrateCancel(); }}
                >
                  &#x2717;
                </button>
              </span>
            </div>
          </InfoBox>
        </div>
        <div id="game-status">
          <div id="game-song-title" />
          <div class="game-controls">
            <div id="game-progress">
              <div id="game-progress-fill" />
            </div>
            <div id="game-quantization-badge" />
          </div>
          <span id="game-elapsed-timer" />
          <button
            id="game-reset-btn"
            class="icon-btn icon-md"
            onClick={() => { props.onGameReset(); }}
            aria-label="Restart song"
          >
            <i data-lucide="rotate-cw" />
          </button>
        </div>
      </div>

      <span id="song-bar-hint" />


    </div>
  );
}
